import {inject, injectable} from 'inversify';

import type {CoreArticleApiModel} from '../../../../api/types/core/models';
import type {Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {CategoryModel} from '../../Category/Category.model';
import type {FileModel} from '../../File/File.model';

import {CategoryMapperJrpc} from '../../Category/mappers/Category.mapper.jrpc';
import {DateTimeMapperJrpc} from '../../Date/mappers/DateTime.mapper.jrpc';
import {FileMapperJrpc} from '../../File/mappers/File.mapper.jrpc';
import {ArticleFactory} from '../Article.factory';
import {ArticleModel} from '../Article.model';
import {ArticleExpandJrpc} from './Article.expand.jrpc';

@injectable()
export class ArticleMapperJrpc
  implements
    IApiDataMapper<CoreArticleApiModel, ArticleModel.Props, ArticleModel>
{
  constructor(
    @inject(CategoryMapperJrpc)
    private readonly categoryMapper: CategoryMapperJrpc,
    @inject(FileMapperJrpc)
    private readonly fileMapper: FileMapperJrpc,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(ArticleFactory)
    private readonly articleFactory: ArticleFactory,
    @inject(ArticleExpandJrpc)
    public readonly expand: ArticleExpandJrpc,
  ) {}

  mapDataToModel(data: ArticleMapperJrpc.ExpandedApiData) {
    return this.articleFactory.create(this.mapDataToProps(data));
  }

  mapDataToProps(data: ArticleMapperJrpc.ExpandedApiData): ArticleModel.Props {
    return {
      avatar: this.mapAvatar(data.avatar),
      categories: this.mapCategories(data.categories),
      content: data.content,
      created: this.dateTimeMapper.mapToModel(data.created),
      files: this.mapFiles(data.attachments),
      id: data._id,
      // TODO: The backend should add this field later
      isFeatured: false,
      status: this.mapStatus(data.status),
      subscription: this.mapSubscription(data.subscription),
      textContent: data.text,
      timeToRead: data.expected_time,
      title: data.title,
      type: this.mapType(data.type),
      updated: this.dateTimeMapper.mapToModel(data.updated),
    };
  }

  private mapAvatar(
    avatar: ArticleMapperJrpc.ExpandedApiData['avatar'],
  ): Nullable<FileModel> {
    return avatar ? this.fileMapper.mapDataToModel(avatar) : null;
  }

  private mapCategories(
    categories: ArticleMapperJrpc.ExpandedApiData['categories'],
  ): CategoryModel[] {
    return categories
      .filter(({category}) => Boolean(category))
      .sort((a, b) => b.weight - a.weight)
      .map(({category}) => this.categoryMapper.mapDataToModel(category!));
  }

  private mapFiles(
    attachments: ArticleMapperJrpc.ExpandedApiData['attachments'],
  ): FileModel[] {
    return attachments
      .filter(Boolean)
      .map(attachment => this.fileMapper.mapDataToModel(attachment));
  }

  private mapStatus(
    status: ArticleMapperJrpc.ExpandedApiData['status'],
  ): ArticleModel.Status {
    switch (status) {
      case 'hidden':
        return ArticleModel.Status.HIDDEN;
      case 'published':
        return ArticleModel.Status.PUBLISHED;
      default:
        return ArticleModel.Status.DRAFT;
    }
  }

  private mapSubscription(
    subscription: ArticleMapperJrpc.ExpandedApiData['subscription'],
  ): ArticleModel.Subscription {
    switch (subscription) {
      case 'premium':
        return ArticleModel.Subscription.PREMIUM;
      default:
        return ArticleModel.Subscription.FREE;
    }
  }

  private mapType(
    type: ArticleMapperJrpc.ExpandedApiData['type'],
  ): ArticleModel.Type {
    switch (type) {
      case 'audio':
        return ArticleModel.Type.AUDIO;
      case 'video':
        return ArticleModel.Type.VIDEO;
      default:
        return ArticleModel.Type.ARTICLE;
    }
  }
}

export namespace ArticleMapperJrpc {
  export type ApiData = CoreArticleApiModel;

  export type ExpandedApiData = ApiData &
    Omit<CoreArticleApiModel, 'categories'> &
    ArticleExpandJrpc.ExpandedFields;
}
